import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Text,
  Group,
  TextInput,
  ScrollArea,
  Badge,
  Paper,
  Stack,
  Avatar,
  ThemeIcon,
  Flex,
  Box,
  Select,
} from "@mantine/core";
import { IconSearch, IconCheck, IconUserPlus } from "@tabler/icons-react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
  collectionGroup,
  increment,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import { toast } from "react-toastify";

interface AssignChallengeModalProps {
  opened: boolean;
  onClose: () => void;
  challenge: any;
}

export default function AssignChallengeModal({
  opened,
  onClose,
  challenge,
}: AssignChallengeModalProps) {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignedMemberIds, setAssignedMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const companyId = useAppSelector(
    (state: RootState) => state.session.company_id,
  );
  const branchId = useAppSelector(
    (state: RootState) => state.session.branch_id,
  );

  useEffect(() => {
    if (opened && companyId) {
      fetchMembers();
      if (challenge?.challenge_type === "team") {
        fetchTeams();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, companyId, branchId, challenge?.id]);

  const fetchTeams = async () => {
    if (!companyId || !challenge?.id) return;
    try {
      const teamsRef = collection(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challenge.id,
        "teams",
      );
      const snap = await getDocs(teamsRef);
      const tData = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      setTeams(tData);
    } catch (error) {
      console.error("Error fetching teams", error);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      let allDocs: any[] = [];
      const assignedSet = new Set<string>();

      // Fetch currently assigned members
      if (companyId && challenge?.id) {
        const participantsRef = collection(
          db,
          "companyprofile",
          companyId,
          "challenge_participants",
          challenge.id,
          "participants",
        );
        const partSnap = await getDocs(participantsRef);
        partSnap.forEach((docSnap) => {
          assignedSet.add(docSnap.id);
        });
      }
      setAssignedMemberIds(assignedSet);

      const isAllBranches =
        !branchId || branchId === "all" || branchId === "all_branches";

      if (isAllBranches) {
        // Company admin — fetch across all branches
        const q = query(
          collectionGroup(db, "members"),
          where("company_id", "==", companyId),
        );
        const snapshot = await getDocs(q);
        snapshot.forEach((doc) => {
          allDocs.push({ id: doc.id, ...doc.data() });
        });
      } else {
        // Multi-branch fetch — derive branches from the challenge itself
        const challengeBranchIds: string[] =
          challenge?.branch_ids ||
          (challenge?.branch_id ? [challenge.branch_id] : []);

        if (challengeBranchIds.length > 0) {
          for (const bId of challengeBranchIds) {
            const q = query(collection(db, "branch", bId, "members"));
            const snapshot = await getDocs(q);
            snapshot.forEach((doc) => {
              allDocs.push({ id: doc.id, ...doc.data() });
            });
          }
        } else {
          // Fallback: all company members
          const q = query(
            collectionGroup(db, "members"),
            where("company_id", "==", companyId),
          );
          const snapshot = await getDocs(q);
          snapshot.forEach((doc) => {
            allDocs.push({ id: doc.id, ...doc.data() });
          });
        }
      }

      // Only show members who have registered (have a uid)
      const membersWithUid = allDocs.filter((m) => !!m.uid);
      setMembers(membersWithUid);
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (member: any) => {
    setAssigningId(member.id);
    try {
      let teamObj: any = null;
      if (challenge?.challenge_type === "team") {
        if (!selectedTeamId) {
          toast.error("Please select a team before assigning.");
          setAssigningId(null);
          return;
        }
        teamObj = teams.find((t) => t.id === selectedTeamId);
        if (!teamObj) {
          toast.error("Selected team not found.");
          setAssigningId(null);
          return;
        }

        const teamSizeLimit = challenge.team_size_limit || 999999;

        // Fetch fresh team doc to prevent stale member count bypass
        const teamRef = doc(
          db,
          "companyprofile",
          companyId,
          "challenge_participants",
          challenge.id,
          "teams",
          teamObj.id,
        );
        const tDoc = await getDoc(teamRef);
        const currentMemberCount = tDoc.exists()
          ? tDoc.data().member_count || 0
          : 0;

        if (currentMemberCount >= teamSizeLimit) {
          toast.error(
            `Cannot assign! This team has a maximum limit of ${teamSizeLimit} members.`,
          );
          setAssigningId(null);
          return;
        }
      }

      const challengeRef = doc(
        db,
        "companyprofile",
        companyId,
        "challenges",
        challenge.id,
      );

      // Check limits before assigning (Only for non-team challenges as team challenges use per-team limits)
      const challengeDoc = await getDoc(challengeRef);
      if (challengeDoc.exists() && challenge?.challenge_type !== "team") {
        const challengeData = challengeDoc.data();
        const memberLimit = challengeData.member_limit || 999999;
        const currentParticipants = challengeData.current_participants || 0;

        if (currentParticipants >= memberLimit) {
          toast.error(
            "Cannot assign! This challenge has reached its maximum participant limit.",
          );
          setAssigningId(null);
          return;
        }
      }

      const uid = member.uid || member.member_id || member.id;
      const parentRef = doc(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challenge.id,
      );

      await setDoc(
        parentRef,
        {
          challenge_id: challenge.id,
          challenge_title: challenge.title || "Unnamed Challenge",
          challenge_status: challenge.status || "active",
          target_value: challenge.target_value || 0,
          goal_type: challenge.goal_type || "volume",
          company_id: companyId || "",
          branch_ids:
            (challenge as any).branch_ids ||
            ((challenge as any).branch_id
              ? [(challenge as any).branch_id]
              : []),
        },
        { merge: true },
      );

      const participantRef = doc(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challenge.id,
        "participants",
        uid,
      );

      // Check if already assigned
      const pDoc = await getDoc(participantRef);
      if (pDoc.exists()) {
        toast.error("This member is already assigned to this challenge.");
        setAssigningId(null);
        return;
      }

      const payload: any = {
        uid: uid,
        member_id: member.member_id || member.id || uid,
        company_id: companyId || "",
        branch_id: member.branch_id || "",
        member_name:
          `${member.first_name || ""} ${member.last_name || ""}`.trim(),
        target_value: challenge.target_value || 0,
        status: "active",
        progress: 0,
        joined_at: serverTimestamp(),
      };

      if (teamObj) {
        payload.team_id = teamObj.id;
        payload.team_name = teamObj.team_name;

        // increment team member count
        const teamRef = doc(
          db,
          "companyprofile",
          companyId,
          "challenge_participants",
          challenge.id,
          "teams",
          teamObj.id,
        );
        await setDoc(teamRef, { member_count: increment(1) }, { merge: true });
      }

      await setDoc(participantRef, payload);

      // Increment the current_participants counter so the app knows someone joined!
      await setDoc(
        challengeRef,
        {
          current_participants: increment(1),
        },
        { merge: true },
      );

      setAssignedMemberIds((prev) => new Set(prev).add(uid));

      toast.success(
        `Successfully assigned challenge to ${member.first_name || "Member"}!`,
      );
    } catch (error) {
      console.error("Error assigning challenge:", error);
      toast.error("Failed to assign challenge. Please try again.");
    } finally {
      setAssigningId(null);
    }
  };

  const filteredMembers = members.filter(
    (m) =>
      `${m.first_name || ""} ${m.last_name || ""}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (m.uid || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text weight={600} size="lg">
          Assign: {challenge?.title}
        </Text>
      }
      size="xl"
    >
      <TextInput
        placeholder="Search by name or UID..."
        icon={<IconSearch size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
        mb="lg"
        size="md"
        radius="md"
      />

      {challenge?.challenge_type === "team" && (
        <Select
          label="Select Team"
          placeholder="Pick a team to assign the member to"
          value={selectedTeamId}
          onChange={setSelectedTeamId}
          data={teams.map((t) => ({
            value: t.id,
            label: t.team_name || "Unnamed Team",
          }))}
          mb="lg"
          required
        />
      )}

      <ScrollArea h={400} offsetScrollbars>
        <Stack spacing="sm">
          {loading ? (
            <Text align="center" color="dimmed" mt="xl">
              Loading members...
            </Text>
          ) : filteredMembers.length === 0 ? (
            <Text align="center" color="dimmed" mt="xl">
              No active members found.
            </Text>
          ) : (
            filteredMembers.map((member) => {
              const uid = member.uid || member.member_id || member.id;
              const isAssigned = assignedMemberIds.has(uid);
              const memberName =
                `${member.first_name || ""} ${member.last_name || ""}`.trim() ||
                "Unknown User";

              return (
                <Paper
                  key={member.id}
                  p="sm"
                  radius="md"
                  withBorder
                  sx={(theme) => ({
                    transition: "box-shadow 0.2s ease",
                    "&:hover": {
                      boxShadow: theme.shadows.sm,
                    },
                  })}
                >
                  <Group position="apart" noWrap>
                    <Group noWrap>
                      <Avatar color="blue" radius="xl" size="md">
                        {memberName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Text weight={500} size="sm">
                          {memberName}
                        </Text>
                        <Group spacing="xs" mt={2}>
                          <Text size="xs" color="dimmed">
                            {uid}
                          </Text>
                          <Badge size="xs" color="green" variant="dot">
                            {member.status || "ACTIVE"}
                          </Badge>
                        </Group>
                      </Box>
                    </Group>

                    {isAssigned ? (
                      <Group spacing={6}>
                        <ThemeIcon
                          color="teal"
                          size="sm"
                          radius="xl"
                          variant="light"
                        >
                          <IconCheck size={14} stroke={3} />
                        </ThemeIcon>
                        <Text color="teal" weight={600} size="sm">
                          Assigned
                        </Text>
                      </Group>
                    ) : (
                      <Button
                        size="xs"
                        radius="md"
                        variant="light"
                        leftIcon={<IconUserPlus size={14} />}
                        onClick={() => handleAssign(member)}
                        loading={assigningId === member.id}
                      >
                        Assign
                      </Button>
                    )}
                  </Group>
                </Paper>
              );
            })
          )}
        </Stack>
      </ScrollArea>
    </Modal>
  );
}
